import type { ReportColumn } from '../models/column.types'
import type { FilterMatchMode, ReportFilter } from '../models/filter.types'

import { isNumericReportColumn, reportFilterSubTypeForColumn } from './reportColumnKind'
import { getReportFilterModeDefinition } from './reportFilterModes'

const LIST_MODES = new Set<FilterMatchMode>(['in', 'not_in', 'between'])
const NUMERIC_MODES = new Set<FilterMatchMode>([
  'equals',
  'not_equals',
  'greater_than',
  'greater_than_or_equal_to',
  'less_than',
  'less_than_or_equal_to',
])

export function emptyReportFilter(): ReportFilter {
  return { field: '', match_mode: 'contains', value: '' }
}

export function cloneReportFilter(filter: ReportFilter): ReportFilter {
  return { ...filter, value: Array.isArray(filter.value) ? [...filter.value] : filter.value }
}

export function initialReportFilterValue(mode: FilterMatchMode): unknown {
  const shape = getReportFilterModeDefinition(mode).valueShape
  return shape === 'none' ? null : shape === 'range' ? ['', ''] : shape === 'multiple' ? [] : ''
}

function isNumericColumn(filter: ReportFilter, columns: readonly ReportColumn[]): boolean {
  const column = columns.find((item) => item.field === filter.field)
  if (column) return isNumericReportColumn(column)
  return filter.sub_type === undefined
    ? false
    : isNumericReportColumn({ sub_type: filter.sub_type })
}

function withResolvedSubType(filter: ReportFilter, columns: readonly ReportColumn[]): ReportFilter {
  const column = columns.find((item) => item.field === filter.field)
  const subType = reportFilterSubTypeForColumn(column) ?? filter.sub_type
  return {
    ...filter,
    value: Array.isArray(filter.value) ? [...filter.value] : filter.value,
    ...(subType === undefined ? {} : { sub_type: subType }),
  }
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'string' && value.trim() === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function hasInputValue(value: unknown, numeric: boolean): boolean {
  if (value === null || value === undefined || String(value).trim() === '') return false
  if (!numeric) return ['boolean', 'number', 'string'].includes(typeof value)
  if (typeof value !== 'number' && typeof value !== 'string') return false
  return Number.isFinite(Number(value))
}

/** Validate the editor value shape before normalization reaches the controller boundary. */
export function isValidReportFilterInput(
  filter: ReportFilter,
  columns: readonly ReportColumn[],
): boolean {
  const shape = getReportFilterModeDefinition(filter.match_mode).valueShape
  if (shape === 'none') return true
  const numeric = isNumericColumn(filter, columns)
  if (shape === 'range') {
    const values = Array.isArray(filter.value)
      ? filter.value
      : String(filter.value ?? '').split(',')
    return values.length === 2 && values.every((value) => hasInputValue(value, numeric))
  }
  if (shape === 'multiple') {
    const values = (
      Array.isArray(filter.value) ? filter.value : String(filter.value ?? '').split(',')
    )
      .map((value) => (typeof value === 'string' ? value.trim() : value))
      .filter((value) => value !== '')
    return values.length > 0 && values.every((value) => hasInputValue(value, numeric))
  }
  return !Array.isArray(filter.value) && hasInputValue(filter.value, numeric)
}

/** Convert editor text into the typed controller filter DTO only at Apply/Save boundaries. */
export function normalizeReportFilterInput(
  filter: ReportFilter,
  columns: readonly ReportColumn[],
): ReportFilter {
  const normalizedFilter = withResolvedSubType(filter, columns)
  if (filter.match_mode === 'is_empty' || filter.match_mode === 'is_not_empty') {
    return { ...normalizedFilter, value: null }
  }
  const numeric = isNumericColumn(filter, columns)
  if (LIST_MODES.has(filter.match_mode)) {
    const values = (
      Array.isArray(filter.value) ? filter.value : String(filter.value ?? '').split(',')
    )
      .map((value) => (typeof value === 'string' ? value.trim() : value))
      .filter((value) => value !== '')
    if (!numeric) return { ...normalizedFilter, value: values.map(String) }
    const parsed = values.map(toNumber)
    if (parsed.some((value) => value === null)) throw new TypeError('Invalid numeric filter value')
    return { ...normalizedFilter, value: parsed as number[] }
  }
  if (numeric && NUMERIC_MODES.has(filter.match_mode)) {
    const value = toNumber(filter.value)
    if (value === null) throw new TypeError('Invalid numeric filter value')
    return { ...normalizedFilter, value }
  }
  return normalizedFilter
}

export function reportFilterInputType(
  filter: ReportFilter,
  columns: readonly ReportColumn[],
): 'text' | 'number' {
  return isNumericColumn(filter, columns) && NUMERIC_MODES.has(filter.match_mode)
    ? 'number'
    : 'text'
}
